import React, { FC, useRef } from "react";
import type { PlatformGreatForSlidesProps } from "@/types";
import { ContentManager } from "../content-manager";
import "swiper/swiper.min.css";
import { Navigation, A11y } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import type { Swiper as SwiperType } from "swiper";
import NextImage from "next/image";
import { SwiperNavigation } from "../swiper";
import SplitTextAnimation from "../split-text-animation";
import { useInView } from "@/hooks/reveal-in-view";

export const PlatformGreatForSlides: FC<PlatformGreatForSlidesProps> = ({
  you,
  customers,
}) => {
  const ref1 = useRef(null);
  const isInView1 = useInView(ref1, { once: true, margin: "0px 0px 25% 0px" });
  const ref2 = useRef(null);
  const isInView2 = useInView(ref2, { once: true, margin: "0px 0px 25% 0px" });
  const [youPaginationWidth, setYouPaginationWidth] = React.useState(0);
  const [
    customersPaginationWidth,
    setCustomersPaginationWidth,
  ] = React.useState(0);

  const handleSwiperSlideChange = (
    e: SwiperType,
    type: "you" | "customers"
  ) => {
    // const navHeight = document.querySelector(".header")?.clientHeight || 0;
    // const elementTop = e.el.getBoundingClientRect().top + window.pageYOffset;
    // const scrollPosition = elementTop - navHeight - 20;

    // window.scrollTo({
    //   top: scrollPosition,
    //   behavior: "smooth",
    // });

    var activeIndex = e.activeIndex;
    var totalSlides = e.slides.length;

    // Calculate the width percentage
    var widthPercentage = ((activeIndex + 1) / totalSlides) * 100;

    // If the width percentage reaches 100, set it to 100
    if (widthPercentage >= 100) {
      widthPercentage = 100;
    }

    if (type === "customers") {
      setCustomersPaginationWidth(widthPercentage);
      return;
    }
    setYouPaginationWidth(widthPercentage);
  };

  React.useEffect(() => {
    const descriptionLists = document.querySelectorAll(
      ".platformGreatForSlides--slide-description .unordered-list"
    );

    descriptionLists.forEach((list) => {
      const listItems = list.querySelectorAll("li");

      for (let index = 0; index < listItems.length; index++) {
        const item = listItems[index];
        const itemTextLength = item.textContent?.length || 0;

        if (itemTextLength > 50) {
          list.classList.add("long");
          break;
        }
      }
    });

    setYouPaginationWidth((0 + 1 / you.length) * 100);
    setCustomersPaginationWidth((0 + 1 / customers.length) * 100);
  }, [customers.length, you.length]);

  return (
    <section className="platformGreatForSlides">
      <div className="platformGreatForSlides--inner">
        <div className="platformGreatForSlides--group platformGreatForSlides--group_you">
          <div className="wrapper">
            <div ref={ref1} className="platformGreatForSlides--top">
              <SplitTextAnimation
                htmlElement={
                  <h2 className="platformGreatForSlides--title">
                    Great for you!
                  </h2>
                }
                isInView={isInView1}
              />
            </div>
          </div>
          <div className="wrapper wrapper_lg">
            <Swiper
              modules={[A11y, Navigation]}
              navigation={{
                prevEl:
                  ".platformGreatForSlides--navigation-1 button:first-of-type",
                nextEl:
                  ".platformGreatForSlides--navigation-1 button:last-of-type",
              }}
              spaceBetween={14}
              slidesPerView={1.1}
              breakpoints={{
                1024: {
                  spaceBetween: 30,
                },
              }}
              grabCursor
              onSlideChange={(swiper) => handleSwiperSlideChange(swiper, "you")}
              className="platformGreatForSlides--swiper"
            >
               <SwiperNavigation
                size="lg"
                className="platformGreatForSlides--navigation platformGreatForSlides--navigation-1"
              />
              {you.map((slide, index) => {
                return (
                  <SwiperSlide
                    key={index}
                    className="platformGreatForSlides--slide"
                  >
                    {slide.title && (
                      <ContentManager
                        content={slide.title}
                        className="platformGreatForSlides--slide-title"
                      />
                    )}
                    {slide.description && (
                      <ContentManager
                        content={slide.description}
                        className="platformGreatForSlides--slide-description"
                      />
                    )}
                    {index % 2 === 0 ? (
                      index === 0 || index % 4 === 0 ? (
                        <NextImage
                          src="/images/spiral.webp"
                          alt="Spiral"
                          width={1417}
                          height={1653}
                          sizes="(max-width: 768px) 150vw, 100vw"
                          draggable={false}
                          className="platformGreatForSlides--slide-image platformGreatForSlides--slide-image_1"
                        />
                      ) : (
                        <NextImage
                          src="/images/spiral-3.webp"
                          alt="Spiral"
                          width={1773}
                          height={1770}
                          sizes="(max-width: 768px) 150vw, 100vw"
                          draggable={false}
                          className="platformGreatForSlides--slide-image platformGreatForSlides--slide-image_3"
                        />
                      )
                    ) : (
                      <></>
                    )}
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
          <div className="wrapper">
            <div className="platformGreatForSlides--pagination">
              <div
                className="platformGreatForSlides--pagination-inner"
                style={{
                  width: youPaginationWidth + "%",
                }}
              />
            </div>
          </div>
        </div>
        <div className="platformGreatForSlides--group platformGreatForSlides--group_customers">
          <div className="wrapper">
            <div ref={ref2} className="platformGreatForSlides--top">
              <SplitTextAnimation
                htmlElement={
                  <h2 className="platformGreatForSlides--title">
                    Great for your customers!
                  </h2>
                }
                isInView={isInView2}
              />
            </div>
          </div>
          <div className="wrapper wrapper_lg">
            <Swiper
              modules={[A11y, Navigation]}
              navigation={{
                prevEl:
                  ".platformGreatForSlides--navigation-2 button:first-of-type",
                nextEl:
                  ".platformGreatForSlides--navigation-2 button:last-of-type",
              }}
              spaceBetween={14}
              slidesPerView={1.1}
              breakpoints={{
                1024: {
                  spaceBetween: 30,
                },
              }}
              grabCursor
              onSlideChange={(swiper) =>
                handleSwiperSlideChange(swiper, "customers")
              }
              className="platformGreatForSlides--swiper"
            >
              <SwiperNavigation
                size="lg"
                className="platformGreatForSlides--navigation platformGreatForSlides--navigation-2"
              />
              {customers.map((slide, index) => {
                return (
                  <SwiperSlide
                    key={index}
                    className="platformGreatForSlides--slide"
                  >
                    {slide.title && (
                      <ContentManager
                        content={slide.title}
                        className="platformGreatForSlides--slide-title"
                      />
                    )}
                    {slide.description && (
                      <ContentManager
                        content={slide.description}
                        className="platformGreatForSlides--slide-description"
                      />
                    )}
                    {index % 2 === 0 ? (
                      index === 0 || index % 4 === 0 ? (
                        <NextImage
                          src="/images/spiral-3.webp"
                          alt="Spiral"
                          width={1773}
                          height={1770}
                          draggable={false}
                          className="platformGreatForSlides--slide-image platformGreatForSlides--slide-image_3"
                        />
                      ) : (
                        <NextImage
                          src="/images/spiral.webp"
                          alt="Spiral"
                          width={1417}
                          height={1653}
                          draggable={false}
                          className="platformGreatForSlides--slide-image platformGreatForSlides--slide-image_1"
                        />
                      )
                    ) : (
                      <></>
                    )}
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
          <div className="wrapper">
            <div className="platformGreatForSlides--pagination">
              <div
                className="platformGreatForSlides--pagination-inner"
                style={{
                  width: customersPaginationWidth + "%",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
